import { colors } from './Colors';
import { DeepPartial, Theme as TwilioTheme, CSSProps } from '@twilio/flex-webchat-ui';
import { ThemeConfig } from '../../../types/webChat.type';

const fontFamily: CSSProps = {
    fontFamily: 'Public Sans, sans-serif !important',
    fontSize: '13px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '16px',
    letterSpacing: '0em',
};

const primaryColors: CSSProps = {
    background: colors.primaryColor,
    color: colors.textLight,
};

const primaryColorsHover: CSSProps = {
    background: colors.primaryColorHover,
    color: colors.textLight,
};

const brandMessageBubbleColors: CSSProps = (backGroundColor: string, textColor: string, fromMe: boolean = false) => ({
    BubbleContainer: {
        maxWidth: '100%',
        maxHeight: '100%',
        margin: '0',
    },
    Bubble: {
        ...fontFamily,
        background: 'none',
        color: textColor,
        width: fromMe ? '88%' : '100%',
        height: '100%',
    },
    Avatar: {
        background: backGroundColor,
        color: textColor,
        display: 'none',
        width: '0',
        height: '0',
    },
    Header: {
        ...fontFamily,
        color: textColor,
    },
});

export const Theme = (themeConfig: ThemeConfig = {}): DeepPartial<TwilioTheme> => ({
    MainContainer: {
        ...fontFamily,
        ...themeConfig.MainContainer,
        width: '280px',
        height: '556px',
        borderRadius: '8px',
    },
    Chat: {
        MessagingCanvas: {
            Container: {
                ...fontFamily,
                background: colors.lightBackground,
            },
        },
        MessageList: {
            DateSeparatorLine: {
                color: colors.textDark,
            },
            TypingIndicator: {
                color: colors.textDark,
            },
            DateSeparatorText: {
                ...fontFamily,
                color: colors.textDark,
            },
        },
        WelcomeMessage: {
            Container: {
                ...fontFamily,
                color: colors.primaryColor,
                align: 'center',
            },
            Icon: {
                display: 'none',
            },
        },
        MessageListItem: {
            FromOthers: brandMessageBubbleColors(colors.baseGreyColor, colors.textDark),
            FromMe: brandMessageBubbleColors(colors.baseLightGreenColor, colors.textLight, true),
            ReadStatus: {
                ...fontFamily,
                color: colors.textDark,
            },
        },
        MessageInput: {
            AttachFileButton: {
                background: 'none',
                color: colors.primaryColor,
                opacity: '1 !important',
                height: '30px',
                width: '30px',
                marginLeft: '-3px',
                marginBottom: '4px',
                borderRadius: '10px',
            },
            FileBox: {
                background: colors.lightGreyBackground,
                borderRadius: '4px',
            },
            Container: {
                background: colors.mainBackground,
                color: colors.textDark,
                ...fontFamily,
                '& .Twilio-AttachFileButton': {
                    background: 'none !important',
                    outline: 'none',
                },
                '& .Twilio-MessageInputArea-TextArea': {
                    width: '176px',
                    borderRadius: '20px',
                    marginLeft: '-6.5px',
                    textarea: {
                        ...fontFamily,
                        lineHeight: '18px',
                    },
                },
                '::placeholder': {
                    color: colors.textDark,
                },
            },
            Button: {
                backgroundColor: colors.mainBackground,
                color: colors.primaryColor,
                opacity: '1 !important',
                '&:hover': {
                    ...primaryColorsHover,
                    backgroundBlendMode: 'color',
                },
            },
        },

        MessageCanvasTray: {
            Button: {
                width: '200px',
                height: '24px',
                ...fontFamily,
                fontWeight: '700',
                color: colors.textLight,
                background: colors.primaryColor,
                borderRadius: '100px',
                padding: '4px 57.5px',
                margin: '0',
            },
            Container: {
                height: '146px',
                width: '280px',
                ...fontFamily,
                background: colors.lightBackground,
                color: colors.textDark,
                '& .Twilio-MessageCanvasTray-default': {
                    width: '100%',
                },
                '& .MessageCanvasTrayContent': {
                    margin: '40px 40px 8px 40px !important',
                },
            },
        },
    },

    MainHeader: {
        Container: {
            ...fontFamily,
            background: colors.lightBackground,
            color: colors.textLight,
            '& .Twilio-Icon-Close': {
                ...themeConfig.CloseButton,
                color: 'rgb(0,0,0)',
                cursor: 'pointer',
                position: 'absolute',
                right: 0,
                top: 0,
            },
            '&:hover': {
                cursor: 'default',
            },
        },
    },

    EntryPoint: {
        Container: {
            overflow: 'initial !important',
            '& .Twilio-Icon-ArrowDown': {
                'svg path:hover': {
                    fill: colors.primaryColor,
                },
            },
            '& .Twilio-Icon-Custom': {
                svg: {
                    paddingTop: '2px',
                    paddingLeft: '2.5px',
                    width: '71px',
                    height: '70px',
                },
            },
            ...themeConfig.EntryPoint,
            ...primaryColors,
            ...fontFamily,
            width: '50px',
            height: '50px',
            boxShadow: '1px 1px 10px 0px #00334973',
            '&:hover': {
                ...primaryColorsHover,
                backgroundBlendMode: 'color',
                '.Twilio-Icon-Custom': {
                    'svg path': {
                        fill: colors.primaryColor + '!important',
                    },
                },
                '.Twilio-Icon-ArrowDown': {
                    'svg path': {
                        fill: colors.primaryColor + '!important',
                    },
                },
            },
        },
    },

    PendingEngagementCanvas: {
        Container: {
            backgroundColor: colors.lightBackground,
        },
    },
});
